import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-router';
import { FormType, SubmissionMode } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader, useAlert } from '@frontend/design-system';
import { buildAutofillValues } from '../../../../../autofill';
import { hasRequiredPatronData } from '../../../../../helpers';
import { useGetFormTemplate } from '../../../../../hooks/api-hooks';
import { useConfigShallowStore } from '../../../../../stores';
import ContentContainer from '../../../../layout/content-container.component';
import Header from '../../../../layout/header.component';
import { DynamicFormContainer } from '../../../components/dynamic-form-container/dynamic-form-container';
import { useSubmitDynamicForm } from '../../../components/dynamic-form-container/hooks/use-submit-dynamic-form';
import { DynamicFormLiveState } from '../../../components/dynamic-form-container/submit-form.helpers';
import { SubmissionConfirmation } from '../../../components/form-components/submission-confirmation';
import { FormNotFound } from '../../../components/form-not-found';
import { PatronForm, PatronFormValues } from '../../../components/patron-form';
import { PatronFormWelcomeScreen } from '../../../components/patron-form-welcome-screen';
import { FillingExperienceProps } from '../../../filling-experience.type';
import { useLocaleSchemaForms } from '../../../hooks/use-locale-schema-forms';
import { FillingExperienceWrapper } from '../../../provider';

type Steps = 'welcome' | 'patron-form' | 'dynamic-form' | 'submitted';

type PublicPacketProps = FillingExperienceProps & {
  isKioskOrigin?: boolean;
};

export const PublicPacket: FC<PublicPacketProps> = (props) => {
  const { locationId, documentId, isKioskOrigin, isPreviewMode } = props;
  const navigate = useNavigate();
  const { t } = useTranslation('digital-forms-experience');
  const alerts = useAlert();

  const { person, setPerson } = useConfigShallowStore('person', 'setPerson');

  const hasPatronData = hasRequiredPatronData(person);

  // Patron data was already collected in the kiosk new/existing-patron flow, so skip straight to the form
  const skipPatronCollection = !!isKioskOrigin && hasPatronData;

  const [currentStep, setCurrentStep] = useState<Steps>(skipPatronCollection ? 'dynamic-form' : 'welcome');

  const moveTo = (step: Steps) => () => setCurrentStep(step);

  useEffect(() => {
    // Missing required patron data means the kiosk flow was skipped/incomplete, so send them back to restart it
    if (isKioskOrigin && !hasPatronData) {
      navigate({ to: `/kiosk/${locationId}` });
    }
  }, [isKioskOrigin, hasPatronData, locationId, navigate]);

  const { formTemplate, isLoading, isError, isNotRetryableError, isEmptyForm, refetch } = useGetFormTemplate(
    {
      packetId: documentId,
      companyId: locationId,
      includeOptions: true,
    },
    { isPreviewMode }
  );

  const schemaForms = useLocaleSchemaForms(formTemplate?.data ?? []);
  const isSubmitted = !!formTemplate?.isSubmitted;

  const { submitForm, isSubmitting, submissionResponse } = useSubmitDynamicForm({
    schemaForms,
    locationId,
    type: FormType.TYPE_PACKET,
    mode: isKioskOrigin ? SubmissionMode.UNSOLICITED_PATIENTKIOSK : SubmissionMode.UNSOLICITED_PUBLICLINKS,
    documentId,
  });

  if (isLoading) {
    return (
      <div
        css={css`
          height: 100vh;
        `}
      >
        <ContentLoader show message='Loading form...' />
      </div>
    );
  }

  if (!formTemplate || isError) {
    if (isNotRetryableError) return <FormNotFound variant='not-found' locationId={locationId} />;
    return <FormNotFound variant='error' onRetry={refetch} locationId={locationId} />;
  }

  const logoUrl = formTemplate.logoUrl ?? '';

  if (isSubmitted && currentStep !== 'submitted') {
    return <FormNotFound variant='already-submitted' locationId={locationId} logoUrl={logoUrl} />;
  }

  if (isEmptyForm) {
    return <FormNotFound variant='empty-form' locationId={locationId} logoUrl={logoUrl} />;
  }

  const initialValues = hasPatronData ? buildAutofillValues({ personDetails: person }) : undefined;

  const handleReviewSubmit = async (liveState: DynamicFormLiveState) => {
    try {
      await submitForm(liveState);
      setCurrentStep('submitted');
    } catch {
      alerts.error(t('Something went wrong submitting your form. Please try again.'));
    }
  };

  const handlePatronFormSubmit = (values: PatronFormValues) => {
    setPerson({
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.mobilePhone,
      birthdate: values.birthdate,
      email: values.email,
      city: values.city,
      state: values.state,
      zipCode: values.postalCode,
      streetAddress1: values.address1,
      streetAddress2: values.address2,
    });
    setCurrentStep('dynamic-form');
  };

  return (
    <FillingExperienceWrapper {...props} experienceType='public' documentType='packet'>
      <Header showCountrySelector={currentStep !== 'submitted'} logoUrl={logoUrl} />
      <ContentContainer>
        {currentStep === 'welcome' && (
          <PatronFormWelcomeScreen locationId={locationId} onAccept={moveTo('patron-form')} />
        )}
        {currentStep === 'patron-form' && <PatronForm onSubmit={handlePatronFormSubmit} patronType='pre-reg' />}
        {currentStep === 'dynamic-form' && (
          <DynamicFormContainer
            schemaForms={schemaForms}
            initialValues={initialValues}
            isSubmitting={isSubmitting}
            onReviewSubmit={handleReviewSubmit}
          />
        )}
        {currentStep === 'submitted' && <SubmissionConfirmation submissionId={submissionResponse?.submissionId} />}
      </ContentContainer>
    </FillingExperienceWrapper>
  );
};
